/**
 * SAAY physical card numbers and expiry (AC §19.6.1, FTD §3.3, §5.3).
 *
 * Card and account numbers stay in ASCII digits for copy and comparison; render them in `<bdi dir="ltr">`.
 * Expiry `expiryText` is Solar Hijri `YY/MM` from the API in ASCII digits; display uses Persian digits.
 */

import { normalizeDigits, toPersianDigits } from './digits.js';
import { MASK_CHAR, displayServerMask } from './masking.js';

const EXPIRY_TEXT_PATTERN = /^(\d{2})\/(\d{2})$/;

function compactDigits(value: string): string {
  return normalizeDigits(value).replace(/\s+/g, '');
}

/** `9900005949239264` → `9900 0059 4923 9264` (16 ASCII digits, 4×4 groups). */
export function formatGroupedCardNumber(cardNumber: string): string {
  const digits = compactDigits(cardNumber);

  if (!/^\d{16}$/.test(digits)) {
    throw new RangeError('formatGroupedCardNumber expects 16 digits.');
  }

  return `${digits.slice(0, 4)} ${digits.slice(4, 8)} ${digits.slice(8, 12)} ${digits.slice(12, 16)}`;
}

/** `9900005949239264` → `•••• •••• •••• ۹۲۶۴` (Persian digits, owner W4 decision). */
export function maskCardNumber(cardNumber: string): string {
  const digits = compactDigits(cardNumber);

  if (!/^\d{16}$/.test(digits)) {
    throw new RangeError('maskCardNumber expects 16 digits.');
  }

  const last4 = digits.slice(-4);

  return `${MASK_CHAR.repeat(4)} ${MASK_CHAR.repeat(4)} ${MASK_CHAR.repeat(4)} ${toPersianDigits(last4)}`;
}

/** A server-masked card number (`************9264`, AC §19.6.2) with the display mask and Persian digits. */
export function displayMaskedCardNumber(masked: string): string {
  return displayServerMask(masked, 'card');
}

/** `9510462496778` → `9510 4624 96778` (13 ASCII digits, 4+4+5 groups). */
export function formatGroupedAccountNumber(accountNumber: string): string {
  const digits = compactDigits(accountNumber);

  if (!/^\d{13}$/.test(digits)) {
    throw new RangeError('formatGroupedAccountNumber expects 13 digits.');
  }

  return `${digits.slice(0, 4)} ${digits.slice(4, 8)} ${digits.slice(8, 13)}`;
}

/**
 * Formats Solar Hijri card expiry as printed on the card: API `expiryText` `YY/MM` (ASCII) → Persian digits.
 * Example: `10/07` → `۱۰/۰۷`.
 */
export function formatPhysicalCardExpiryText(expiryText: string): string {
  if (!EXPIRY_TEXT_PATTERN.test(expiryText)) {
    throw new RangeError('formatPhysicalCardExpiryText expects YY/MM in ASCII digits.');
  }

  return toPersianDigits(expiryText);
}

/**
 * Normalizes typed card expiry (Persian or Latin digits; `/`, `-`, or no separator) to API `YY/MM` ASCII,
 * or `null` when the month is not 01–12 or the shape is not two year digits and two month digits.
 */
export function normalizeCardExpiryInput(input: string): string | null {
  const normalized = compactDigits(input).replace(/-/g, '/');
  const separated = /^(\d{2})\/(\d{1,2})$/.exec(normalized);
  const compact =
    separated === null && /^\d{4}$/.test(normalized)
      ? { month: normalized.slice(2, 4), year: normalized.slice(0, 2) }
      : separated === null
        ? null
        : { month: separated[2] ?? '', year: separated[1] ?? '' };

  if (compact === null) {
    return null;
  }

  const month = Number(compact.month.padStart(2, '0'));
  if (month < 1 || month > 12) {
    return null;
  }

  return `${compact.year}/${String(month).padStart(2, '0')}`;
}
